<template>
  <b-card v-loading="status !== ''" :element-loading-text="loadingText">
    <h4>
      Subscription Plan
      <small v-if="status !== 'loading'" class="pull-right">
        <b-badge v-if="subscription.active" variant="success">ACTIVE</b-badge>
        <b-badge v-else-if="trialDays > 0" variant="danger">INACTIVE</b-badge>
        <b-badge v-else variant="danger">EXPIRED</b-badge>
      </small>
    </h4>

    <div class="mt-3" v-if="status !== 'loading'">
      <b-alert :show="cancelled" dismissible fade variant="success" @dismissed="cancelled = false" @dismiss-count-down="(i) => cancelled = i">Your subscription has been cancelled!</b-alert>
      <b-alert :show="subscribed" dismissible fade variant="success" @dismissed="subscribed = false" @dismiss-count-down="(i) => subscribed = i">Your subscription has been activated!</b-alert>
      <cancel v-if="cancelling" @back="cancelling = false" @cancel="cancelSubscription" />
      <div v-else-if="subscription.active">
        <p>You are currently subscribed to the <strong>{{subscription.name}}</strong> plan which includes unlimited listings and posting schedules. Your advertisements are being distributed automatically.</p>

        <b-row class="mt-4 mb-4">
          <b-col :md="6" :offset-md="3">
            <b-card class="price-card w-100" border-variant="primary" body-class="text-center">
              <div v-if="subscription.ribbon" class="ribbon" :class="{ 'is-danger': subscription.cancelled }"><span>{{subscription.ribbon}}</span></div>
              <h4>{{subscription.name}}</h4>
              <div class="price text-primary">
                <sup class="currency">$</sup>
                <span>{{subscription.price.unit}}</span>
                <sup class="decimal">.{{subscription.price.decimal}}</sup>
              </div>
              <div class="interval">per {{subscription.interval}}</div>
            </b-card>
          </b-col>
        </b-row>

        <p v-if="subscription.cancelled">Your subscription has been cancelled but you will continue to have access to your account until <strong v-b-tooltip.hover :title="nextPaymentDate">{{nextPayment}}</strong>.</p>
        <p v-else-if="trialDays < 1">Your subscription will automatically renew <strong v-b-tooltip.hover :title="nextPaymentDate">{{nextPayment}}</strong>.</p>
        <p v-else>Your free trial will end <strong v-b-tooltip.hover :title="nextPaymentDate">{{nextPayment}}</strong>. After that, you will be charged the above amount each {{subscription.interval}}.</p>
        <p v-if="!subscription.cancelled" class="mb-0">If you wish to cancel your subscription, you can do so by <b-link @click="cancelling = true">clicking here</b-link>.</p>
      </div>
      <div v-else>
        <p v-if="newTrial">
          You are <strong>not currently subscribed</strong> to any plan. You can link your publisher accounts and create listings but they will <u>not post automatically</u> at your scheduled times.
          Active your <strong>{{trialDays}}-day Free Trial</strong> now to start publishing your listings!
        </p>
        <p v-else-if="trialDays > 0">
          You have previously cancelled your <strong>TRIAL</strong> plan. You can link your publisher accounts and create listings but they will <u>not post automatically</u> at your scheduled times.
          Subscribe now to continue the remaining <strong>{{trialDays}} day{{trialDays !== 1 ? 's' : ''}}</strong> of the Free Trial and continue publishing your listings!
        </p>
        <p v-else>
          Your subscription <strong>has expired</strong>. You can link your publisher accounts and create listings but they will <u>not post automatically</u> at your scheduled times.
          Re-subscribe now to continue publishing your listings!
        </p>

        <!-- @TODO One day... https://codepen.io/movibe/pen/MvWbvy -->
        <b-alert :show="plans.length === 0 && loadingText === ''" variant="danger" class="my-2">There are no available plans at this time.</b-alert>
        <b-row v-if="plans.length > 0" class="mt-4 price-cards">
          <b-col v-for="(plan, idx) in plans" :key="idx" :md="6" :lg="12" :xl="6" class="d-flex">
            <b-card class="price-card w-100 mb-4" body-class="d-flex flex-grow-1 flex-column justify-content-between text-center">
              <div v-if="plan.ribbon" class="ribbon"><span>{{plan.ribbon}}</span></div>
              <h4>{{plan.name}}</h4>
              <div class="price">
                <sup class="currency">$</sup>
                <span>{{plan.price.unit}}</span>
                <sup class="decimal">.{{plan.price.decimal}}</sup>
              </div>
              <div class="interval">per {{plan.interval}}</div>
              <b-button class="mt-4" variant="primary" size="lg" @click="subscribe(plan)">{{newTrial ? 'Start Free Trial' : 'Subscribe Now'}}</b-button>
            </b-card>
          </b-col>
        </b-row>
        <b-alert :show="newTrial || trialDays > 0" variant="info">Your card will <strong><u>NOT</u></strong> be charged until your trial has ended.</b-alert>

        <a href="https://stripe.com/ca" target="_blank" rel="noreferrer nofollow"><img class="powered-by" src="/img/payments/powered_by_stripe.png" /></a>
        <p v-if="newTrial" class="small mt-3 mb-0">By clicking Start Free Trial above you agree to the AdReplay&reg; <b-link to="https://adreplay.com/terms">terms and conditions</b-link>. Your free trial will begin from the time of activation and will continue for the next <strong>{{trialDays}} day{{trialDays !== 1 ? 's' : ''}}</strong>. You may cancel your subscription any time before the end of the trial period to avoid any charges. Once the trial period has ended, your subscription will renew automatically monthly/annually until cancellation. <b-link to="/support">Click here</b-link> to learn more about our plans and <a href="https://stripe.com/ca" target="_blank" rel="noreferrer nofollow">Stripe.</a></p>
        <p v-else-if="trialDays > 0" class="small mt-3 mb-0">By clicking Subscribe Now above you agree to the AdReplay&reg; <b-link to="https://adreplay.com/terms">terms and conditions</b-link>. Your free trial will resume from the time of first activation and will continue for the next <strong>{{trialDays}} day{{trialDays !== 1 ? 's' : ''}}</strong>. You may cancel your subscription any time before the end of the trial period to avoid any charges. Once the trial period has ended, your subscription will renew automatically monthly/annually until cancellation. <b-link to="/support">Click here</b-link> to learn more about our plans and <a href="https://stripe.com/ca" target="_blank" rel="noreferrer nofollow">Stripe.</a></p>
        <p v-else class="small mt-3 mb-0">By clicking Subscribe Now above you agree to the AdReplay&reg; <b-link to="https://adreplay.com/terms">terms and conditions</b-link>. You may cancel your subscription any time before the end of the trial period to avoid any charges. Your subscription will renew on automatically monthly/annually until cancellation. <b-link to="/support">Click here</b-link> to learn more about our plans and <a href="https://stripe.com/ca" target="_blank" rel="noreferrer nofollow">Stripe.</a></p>
        <b-alert :show="!!error" fade variant="danger" class="mt-4 mb-0">{{error}}</b-alert>
      </div>
    </div>
    <div v-else style="min-height: 120px"></div>
  </b-card>
</template>

<script>
import moment from "moment";
import Cancel from "./subscription/Cancel";

const LoadingText = "Retrieving subscription...";
const PlansText = "Loading available subscriptions...";
const StripeText = "Waiting for Stripe...";
const UpdatingText = "Processing subscription...";
const CancellingText = "Cancelling subscription...";

export default {
  name: "AccountSubscription",

  components: {
    Cancel
  },

  data() {
    return {
      subscription: {},
      plans: [],

      // State
      loadingText: LoadingText,
      error: "",
      cancelling: false,
      cancelled: false,
      subscribed: false
    };
  },

  mounted() {
    this.stripe = window.StripeCheckout.configure({
      key: process.env.VUE_APP_STRIPE_KEY,
      image: "/img/logo-symbol.png",
      locale: "auto",
      token: this.onToken
    });

    this.load();
  },

  beforeDestroy() {
    this.stripe.close();
  },

  computed: {
    status() {
      switch(this.loadingText) {
        case LoadingText:
          return "loading";
        case PlansText:
          return "plans";
        case StripeText:
          return "stripe";
        case UpdatingText:
          return "updating";
        case CancellingText:
          return "cancelling";
      }

      return "";
    },

    nextPayment() {
      return moment(this.subscription.nextPayment).fromNow();
    },

    nextPaymentDate(){
      return moment(this.subscription.nextPayment).format('MMMM Do, YYYY')
    },

    // @TODO load from API
    trialDays() {
      let plan = this.plans[0] || {};
      return this.trialDaysLeft || plan.trialDays || 0;
    },

    newTrial() {
      return this.$session.get("account.newTrial");
    },

    trialDaysLeft() {
      return this.$session.get("account.trialDays");
    }
  },

  methods: {
    load() {
      this.loadingText = LoadingText;
      this.$API.v2.Account.SubscriptionInfo()
        .then(subscription => {
          this.subscription = subscription;
          this.loadingText = "";
        })
        .catch(err => {
          this.error = err;

          // Load subscription plans
          return this.loadPlans();
        });
    },

    loadPlans() {
      this.loadingText = PlansText;
      this.$API.v2.Account.SubscriptionPlans()
        .then(plans => {
          this.plans = plans;
        })
        .catch(err => {
          this.error = err;
        })
        .then(() => {
          this.loadingText = "";
        });
    },

    subscribe(plan) {
      this.loadingText = StripeText;
      this.plan = plan;

      // Open Checkout with further options:
      this.stripe.open({
        name            : "AdReplay",
        description     : plan.interval === "month" ? "Monthly Subscription" : "Annual Subscription",
        panelLabel      : this.newTrial ? "Activate Trial {{amount}}"        : "Subscribe for {{amount}}",
        allowRememberMe : false,
        currency        : "cad",
        amount          : plan.price.total,
        email           : this.$session.get("email"),
        closed          : () => {
          // Closed before token was received
          if(this.loadingText == StripeText) {
            this.loadingText = "";
          }
        }
      });
    },

    onToken(token) {
      // You can access the token ID with `token.id`.
      // Get the token ID to your server-side code for use.
      this.loadingText = UpdatingText;
      this.error = "";

      this.$API.v2.Account.ActivateSubscription(this.plan.id, token.id)
        .then(subscription => {
          this.subscription = subscription;
          this.subscribed = 5;
          this.$emit("subscription-changed", subscription);

          window.fbq('track', 'AddPaymentInfo');
        })
        .catch(err => {
          this.error = err;
        })
        .then(() => {
          this.loadingText = "";
        });
    },

    cancelSubscription(reason, other) {
      this.loadingText = CancellingText;
      this.error = "";

      this.$swal({
        title: "Authentication Required",
        text: "To continue, please enter your password.",
        input: "password",
        customClass: "authentication-modal",
        showCloseButton: true,
        preConfirm: value => {
          // Cancel with password and survey data
          return this.$API.v2.Account.CancelSubscription(value, reason, other)
            .then(() => {
              this.subscription.active = false;
              this.cancelling = false;
              this.cancelled = 5;
              this.loadPlans();
              this.$emit("subscription-changed", false);
            })
            .catch(err => {
              this.$swal.showValidationMessage(err);
            });
        }
      })
        .then(result => {
          if(result.dismiss) {
            this.cancelling = false;
          }
        })
        .catch(err => {
          this.error = err;
        })
        .then(() => {
          this.loadingText = "";
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.price-card {
  margin-bottom: 0;
  position: relative;

  .price {
    font-weight: 200;

    .currency {
      font-size: 1.5rem;
      top: -1.25rem;
    }

    > span {
      font-size: 3rem;
    }

    .decimal {
      font-size: 0.75rem;
      top: -1.65rem;
    }
  }

  .interval {
    font-size: 0.75rem;
    color: #888888;
  }
}

@media (max-width: 576px) {
  .price-card {
    margin-bottom: 1.5rem;
  }

  .price-cards [class*="col-"]:last-of-type {
    .price-card {
      margin-bottom: 0;
    }
  }
}

.ribbon {
  position: absolute;
  right: -5px;
  top: -5px;
  z-index: 1;
  overflow: hidden;
  width: 90px;
  height: 90px;
  text-align: right;

  &.is-danger{
    $color: #c50e0e;

    span{
      background: $color;

      &::before{
        border-left: 3px solid $color;
        border-top: 3px solid $color;
      }

      &::after{
        border-right: 3px solid $color;
        border-top: 3px solid $color;
      }
    }
  }

  $color: #79a70a;

  span {
    font-size: 11px;
    color: #fff;
    text-transform: uppercase;
    text-align: center;
    font-weight: bold;
    line-height: 24px;
    transform: rotate(45deg);
    -webkit-transform: rotate(45deg); /* Needed for Safari */
    width: 120px;
    display: block;
    background: $color;
    // background: linear-gradient(#9BC90D 0%, #79A70A 100%);
    box-shadow: 0 3px 10px -5px rgba(0, 0, 0, 1);
    position: absolute;
    top: 22px;
    right: -26px;

    &::before {
      content: "";
      position: absolute;
      left: 0px;
      top: 100%;
      z-index: -1;
      border-left: 3px solid $color;
      border-right: 3px solid transparent;
      border-bottom: 3px solid transparent;
      border-top: 3px solid $color;
    }

    &::after {
      content: "";
      position: absolute;
      right: 0%;
      top: 100%;
      z-index: -1;
      border-right: 3px solid $color;
      border-left: 3px solid transparent;
      border-bottom: 3px solid transparent;
      border-top: 3px solid $color;
    }
  }
}

.powered-by {
  height: 30px;
}
</style>

<style lang="scss">
.cancel-reasons {
  .custom-radio {
    margin: 0.25rem 0;
  }
}
</style>
